import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import BreadCrumbs from '../components/Breadcrumbs'
import Spinner from '../components/Spinner'
import { Container } from '../components/Animations'
import Radom, { ManagedBalanceLedger, Swap as ISwap, SwapStatus, TransactionStatus } from '../state/Radom'
import { ledgerBalanceLabel, ledgerTicker } from '../util/Ledger'
import { assetLineFor } from '../util/AssetLabels'
import { errorToast, formatAssetAmount, shortAddress, unifyLedger } from '../util/Util'
import { getMethod } from '../util/Managed'
import { Section, SectionContent, SectionItem, SubTitle } from '../components/Shared'
import TimeText from '../components/TimeText'
import CopyButton from '../components/CopyButton'
import LinkExternal from '../components/LinkExternal'
import { ExternalLinkText } from './Events'
import { formatExplorerUrl } from '../util/Chains'
import { TransactionStatusBadge } from './Transactions'
import {
  containerStyle,
  headerRowStyle,
  ledgerMutedTextStyle,
  ledgerRowStyle,
  ledgerTitleStyle,
  linkExternalIconStyle,
  loadingWrapperStyle,
  payoutAddressCellStyle,
  payoutHeaderStyle,
  payoutLogoStyle,
  tickerBadgeStyle,
  txLinkStyle
} from './PayoutStyles'

/** The balance a ledger row is for: "USDC on Polygon", or "USDC · All networks" for a pooled balance. */
const swapStatusToTransactionStatus = (status: SwapStatus): TransactionStatus => {
  switch (status) {
  case SwapStatus.Finalized:
    return TransactionStatus.Successful
  case SwapStatus.Failed:
    return TransactionStatus.Failed
  default:
    return TransactionStatus.Pending
  }
}

const SwapView = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')

  const [swap, setSwap] = useState<ISwap>()
  const [isLoading, setIsLoading] = useState(true)
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger[]>()

  useEffect(() => {
    if (!params.id) return
    const fetchSwap = async (): Promise<void> => {
      setIsLoading(true)
      try {
        const swapResp = await Radom.getSwap(params.id as string)
        setSwap(swapResp)
      } catch {
        setIsLoading(false)
        errorToast('Failed to load swap')
        return
      }

      try {
        const ledger = await Radom.listManagedBalanceLedger([params.id as string])
        if (ledger.length > 0) {
          setManagedBalanceLedger(ledger.length === 1 ? ledger : unifyLedger(ledger))
        }
      } catch {
        errorToast('Failed to load ledger')
      }

      setIsLoading(false)
    }
    void fetchSwap()
  }, [params.id])

  const fromMethod = useMemo(
    () => swap ? getMethod(swap.fromNetwork, swap.fromToken) : undefined,
    [swap]
  )
  const toMethod = useMemo(
    () => swap ? getMethod(swap.toNetwork, swap.toToken) : undefined,
    [swap]
  )

  if (isLoading) {
    return (
      <div style={loadingWrapperStyle}>
        <Spinner />
      </div>
    )
  }

  if (!swap || !fromMethod || !toMethod) {
    return (
      <div>
        <span>Swap not found</span>
      </div>
    )
  }

  const status = swap.fundsAvailable && swap.status !== SwapStatus.Failed
    ? TransactionStatus.Successful
    : swapStatusToTransactionStatus(swap.status)
  const feeAmount = swap.swapFee !== undefined && swap.swapFee !== null
    ? Number(swap.swapFee)
    : undefined

  return (
    <Container style={containerStyle}>
      <div style={headerRowStyle}>
        <BreadCrumbs
          items={
            source === 'transactions'
              ? [
                { item: 'Transactions', to: '/transactions' },
                { item: swap.id, to: `/swap/${swap.id}?source=transactions` }
              ]
              : [
                { item: 'Transactions', to: '/transactions' },
                { item: swap.id, to: '.' }
              ]
          }
        />
      </div>

      <Section>
        <div style={{ ...payoutHeaderStyle, gap: 8 }}>
          <img src={fromMethod.logo} style={payoutLogoStyle} />
          <span>{formatAssetAmount(swap.fromAmount, fromMethod.ticker)}</span>
          <span style={tickerBadgeStyle}>{fromMethod.ticker}</span>
          <span style={{ color: '#888' }}>→</span>
          <img src={toMethod.logo} style={payoutLogoStyle} />
          <span>
            {swap.toAmount ? formatAssetAmount(swap.toAmount, toMethod.ticker) : '—'}
          </span>
          <span style={tickerBadgeStyle}>{toMethod.ticker}</span>
        </div>

        <SectionContent>
          <SectionItem>
            <SubTitle>Status</SubTitle>
            <TransactionStatusBadge status={status}>
              {swap.fundsAvailable && swap.status !== SwapStatus.Finalized && swap.status !== SwapStatus.Failed
                ? 'Complete'
                : swap.status}
            </TransactionStatusBadge>
          </SectionItem>

          <SectionItem>
            <SubTitle>Swap ID</SubTitle>
            <div style={payoutAddressCellStyle}>
              <span>{shortAddress(swap.id, 8)}</span>
              <CopyButton text={swap.id} />
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Radom fee</SubTitle>
            <span>
              {feeAmount !== undefined
                ? `${formatAssetAmount(feeAmount, fromMethod.ticker)} ${fromMethod.ticker}`
                : '-'}
            </span>
          </SectionItem>

          <SectionItem>
            <SubTitle>Created</SubTitle>
            <TimeText date={new Date(swap.createdAt)} />
          </SectionItem>

          <SectionItem>
            <SubTitle>Last updated</SubTitle>
            <TimeText date={new Date(swap.updatedAt)} />
          </SectionItem>
        </SectionContent>

        {swap.failReason && (
          <SectionContent>
            <SectionItem>
              <SubTitle>Failure reason</SubTitle>
              <span>{swap.failReason}</span>
            </SectionItem>
          </SectionContent>
        )}
      </Section>

      <Section>
        <SubTitle style={ledgerTitleStyle}>Source</SubTitle>
        <SectionContent>
          <SectionItem>
            <SubTitle>Asset</SubTitle>
            <span>{assetLineFor(fromMethod.ticker, swap.fromNetwork)}</span>
          </SectionItem>

          <SectionItem>
            <SubTitle>Amount</SubTitle>
            <span>{formatAssetAmount(swap.fromAmount, fromMethod.ticker)} {fromMethod.ticker}</span>
          </SectionItem>

          <SectionItem>
            <SubTitle>Transaction hash</SubTitle>
            {swap.fromTxid
              ? <div style={payoutAddressCellStyle}>
                <ExternalLinkText
                  style={txLinkStyle}
                  target="_blank"
                  rel="noreferrer"
                  href={formatExplorerUrl(swap.fromNetwork, 'tx', swap.fromTxid)}
                >
                  {shortAddress(swap.fromTxid, 8)}
                  <LinkExternal stroke={linkExternalIconStyle.stroke} width={linkExternalIconStyle.width} />
                </ExternalLinkText>
                <CopyButton text={swap.fromTxid} />
              </div>
              : <span>-</span>}
          </SectionItem>
        </SectionContent>
      </Section>

      <Section>
        <SubTitle style={ledgerTitleStyle}>Destination</SubTitle>
        <SectionContent>
          <SectionItem>
            <SubTitle>Asset</SubTitle>
            <span>{assetLineFor(toMethod.ticker, swap.toNetwork)}</span>
          </SectionItem>

          <SectionItem>
            <SubTitle>Amount</SubTitle>
            <span>
              {swap.toAmount
                ? `${formatAssetAmount(swap.toAmount, toMethod.ticker)} ${toMethod.ticker}`
                : '-'}
            </span>
          </SectionItem>

          <SectionItem>
            <SubTitle>Transaction hash</SubTitle>
            {swap.toTxid
              ? <div style={payoutAddressCellStyle}>
                <ExternalLinkText
                  style={txLinkStyle}
                  target="_blank"
                  rel="noreferrer"
                  href={formatExplorerUrl(swap.toNetwork, 'tx', swap.toTxid)}
                >
                  {shortAddress(swap.toTxid, 8)}
                  <LinkExternal stroke={linkExternalIconStyle.stroke} width={linkExternalIconStyle.width} />
                </ExternalLinkText>
                <CopyButton text={swap.toTxid} />
              </div>
              : <span>-</span>}
          </SectionItem>
        </SectionContent>
      </Section>

      {managedBalanceLedger && managedBalanceLedger.length > 0 && (
        <Section>
          <SubTitle style={ledgerTitleStyle}>Ledger</SubTitle>

          {managedBalanceLedger.map((item, idx) => {
            const ticker = ledgerTicker(item)

            return (
              <SectionContent key={idx}>
                <SectionItem>
                  <SubTitle>Balance before</SubTitle>
                  <div style={ledgerRowStyle}>
                    <span>{formatAssetAmount(item.balanceBefore, ticker)}</span>
                    <span style={ledgerMutedTextStyle}>
                      {ledgerBalanceLabel(item, ticker)}
                    </span>
                  </div>
                </SectionItem>

                <SectionItem>
                  <SubTitle>Balance after</SubTitle>
                  <div style={ledgerRowStyle}>
                    <span>{formatAssetAmount(item.balanceAfter, ticker)}</span>
                    <span style={ledgerMutedTextStyle}>
                      {ledgerBalanceLabel(item, ticker)}
                    </span>
                  </div>
                </SectionItem>
              </SectionContent>
            )
          })}
        </Section>
      )}
    </Container>
  )
})

export default SwapView
